import type { DealItemWithVersions } from '@/lib/graphql/generated/graphql';
import {
  ALL_HIGHLIGHTABLE_FIELDS,
  type ProductModification,
} from '@/states/recently-modified-products.state';

type ItemsById = Map<string, DealItemWithVersions>;

type ActiveVersion = DealItemWithVersions['activeVersion'];

/**
 * Valeur comparable d'un champ surlignable.
 *
 * `productCategory` est un objet : on le réduit à son identifiant. Les dates sont
 * reconstruites à chaque lecture du flux (`convertDateFields`), donc comparer les
 * instances signalerait un changement à chaque refresh — on compare l'instant.
 */
function comparableFieldValue(
  activeVersion: ActiveVersion,
  field: string
): unknown {
  if (field === 'productCategory') {
    return activeVersion.productCategory?.productCategoryId ?? null;
  }

  const value = (activeVersion as unknown as Record<string, unknown>)[field];
  if (value instanceof Date) return value.getTime();
  return value ?? null;
}

/**
 * Champs surlignables que le flux d'items ne transporte pas, donc indifférentiables.
 *
 * `description` n'est pas dans le flux (seul `hasDescription` l'est), mais la table
 * réinjecte son texte dans `productsMap` après avoir chargé les descriptions. Le
 * comparer verrait donc "texte -> absent" à chaque relecture et marquerait la
 * colonne comme modifiée en permanence sur tous les items qui en ont une.
 */
const FIELDS_ABSENT_FROM_ITEMS_STREAM = new Set(['description']);

function changedFieldsBetween(
  previous: ActiveVersion,
  current: ActiveVersion
): string[] {
  return ALL_HIGHLIGHTABLE_FIELDS.filter(
    (field) =>
      !FIELDS_ABSENT_FROM_ITEMS_STREAM.has(field) &&
      comparableFieldValue(previous, field) !==
        comparableFieldValue(current, field)
  );
}

/**
 * Déduit les modifications à surligner en comparant l'état des items avant et
 * après une relecture serveur.
 *
 * Remplace les `changedFields` que poussait la subscription WebSocket
 * `dealItemsUpdatedV3` : le signal SSE ne transporte aucune donnée d'item, donc le
 * champ modifié est dérivé de l'état. C'est ce qui permet de surligner les champs
 * **recalculés par le backend** (le prix Choose final après un changement de
 * remise), que le surlignage optimiste local ne connaît pas.
 *
 * Un item absent d'`previous` et présent dans `current` est traité comme créé et
 * surligné sur tous les champs — c'est le comportement du chemin WebSocket, dont
 * le fallback `ALL_HIGHLIGHTABLE_FIELDS` s'appliquait toujours pour
 * `itemsCreated` / `datasourceProcessed`, le backend n'envoyant jamais de
 * `changedFields` pour ces actions.
 *
 * Un item dont l'objet `activeVersion` est identique par référence est court-circuité
 * sans comparer les champs. Les items disparus sont ignorés : il n'y a plus de
 * cellule à surligner.
 *
 * Limite connue : `description` n'est pas transporté par le flux d'items (seul
 * `hasDescription` l'est), donc une édition de description distante n'est pas
 * détectable ici. Les éditions locales restent surlignées par
 * `useExecuteOptimisticUpdate`.
 */
export function diffHighlightableFields({
  previous,
  current,
}: {
  previous: ItemsById;
  current: ItemsById;
}): ProductModification[] {
  const modifications: ProductModification[] = [];

  for (const [id, currentItem] of current) {
    const previousItem = previous.get(id);

    if (!previousItem) {
      modifications.push({ productId: id, fields: ALL_HIGHLIGHTABLE_FIELDS });
      continue;
    }

    if (previousItem.activeVersion === currentItem.activeVersion) continue;

    const fields = changedFieldsBetween(
      previousItem.activeVersion,
      currentItem.activeVersion
    );
    if (fields.length > 0) modifications.push({ productId: id, fields });
  }

  return modifications;
}
